import { useState, useEffect, useRef, useCallback } from 'react'
import { canvasCommentsApi, type ApiCanvasComment } from '../utils/api'
import { useEditorStore } from '../store/editor.store'

interface Props {
  siteId: string | null
  enabled: boolean
}

interface PendingPin {
  x: number
  y: number
}

export function CanvasComments({ siteId, enabled }: Props) {
  const { page } = useEditorStore()
  const [comments, setComments] = useState<ApiCanvasComment[]>([])
  const [pending, setPending] = useState<PendingPin | null>(null)
  const [draftText, setDraftText] = useState('')
  const [saving, setSaving] = useState(false)
  const [activeComment, setActiveComment] = useState<string | null>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const load = useCallback(() => {
    if (!siteId) return
    canvasCommentsApi.list(siteId, page.slug).then(setComments).catch(() => null)
  }, [siteId, page.slug])

  useEffect(() => { if (enabled) load() }, [load, enabled])

  function handleOverlayClick(e: React.MouseEvent<HTMLDivElement>) {
    if (!enabled || !siteId) return
    const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) * 100
    const y = ((e.clientY - rect.top) / rect.height) * 100
    setPending({ x, y })
    setDraftText('')
    setActiveComment(null)
    setTimeout(() => inputRef.current?.focus(), 50)
  }

  async function submitComment() {
    if (!siteId || !pending || !draftText.trim()) return
    setSaving(true)
    try {
      const c = await canvasCommentsApi.create(siteId, {
        pageSlug: page.slug,
        text: draftText.trim(),
        x: pending.x,
        y: pending.y,
      })
      setComments(prev => [c, ...prev])
      setPending(null)
      setDraftText('')
    } finally {
      setSaving(false)
    }
  }

  async function resolveComment(id: string) {
    if (!siteId) return
    await canvasCommentsApi.resolve(siteId, id)
    setComments(prev => prev.map(c => c.id === id ? { ...c, resolved: true } : c))
    setActiveComment(null)
  }

  async function deleteComment(id: string) {
    if (!siteId) return
    await canvasCommentsApi.delete(siteId, id)
    setComments(prev => prev.filter(c => c.id !== id))
    setActiveComment(null)
  }

  if (!enabled) return null

  const active = comments.filter(c => !c.resolved)
  const resolved = comments.filter(c => c.resolved)

  return (
    <>
      {/* Transparent clickable overlay on top of canvas */}
      <div
        ref={overlayRef}
        onClick={handleOverlayClick}
        style={{ position: 'absolute', inset: 0, zIndex: 200, cursor: 'crosshair' }}
      />

      {/* Comment pins */}
      {active.map(c => (
        <div
          key={c.id}
          style={{
            position: 'absolute',
            left: `${c.x}%`,
            top: `${c.y}%`,
            zIndex: 201,
            transform: 'translate(-50%, -50%)',
          }}
          onClick={e => { e.stopPropagation(); setActiveComment(activeComment === c.id ? null : c.id); setPending(null) }}
        >
          <div style={{
            width: '28px', height: '28px', borderRadius: '50% 50% 50% 0',
            background: '#5b6af0', color: '#fff', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: '12px', fontWeight: 700, cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)', transform: 'rotate(-45deg)',
            border: '2px solid #fff',
          }}>
            <span style={{ transform: 'rotate(45deg)' }}>💬</span>
          </div>

          {activeComment === c.id && (
            <div
              onClick={e => e.stopPropagation()}
              style={{
                position: 'absolute', top: '32px', left: '0', width: '240px',
                background: '#fff', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                border: '1px solid #e5e7eb', padding: '12px', zIndex: 202,
              }}
            >
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#5b6af0', marginBottom: '4px' }}>
                {c.userName}
                <span style={{ color: '#9ca3af', fontWeight: 400, marginLeft: '6px' }}>
                  {new Date(c.createdAt).toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              <div style={{ fontSize: '13px', color: '#111', lineHeight: 1.5, marginBottom: '10px', whiteSpace: 'pre-wrap' }}>
                {c.text}
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  onClick={() => resolveComment(c.id)}
                  style={{ flex: 1, padding: '5px', background: '#dcfce7', color: '#15803d', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}
                >✓ Решено</button>
                <button
                  onClick={() => deleteComment(c.id)}
                  style={{ padding: '5px 8px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}
                >✕</button>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* New comment input */}
      {pending && (
        <div
          style={{
            position: 'absolute',
            left: `${pending.x}%`,
            top: `${pending.y}%`,
            zIndex: 202,
            transform: 'translate(-50%, -50%)',
          }}
          onClick={e => e.stopPropagation()}
        >
          <div style={{
            width: '28px', height: '28px', borderRadius: '50% 50% 50% 0',
            background: '#f59e0b', border: '2px solid #fff', transform: 'rotate(-45deg)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
          }} />
          <div style={{
            position: 'absolute', top: '32px', left: '0', width: '240px',
            background: '#fff', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            border: '1px solid #e5e7eb', padding: '12px',
          }}>
            <textarea
              ref={inputRef}
              value={draftText}
              onChange={e => setDraftText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submitComment() }}
              placeholder="Оставить комментарий... (Ctrl+Enter)"
              rows={3}
              style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '6px 8px', fontSize: '12px', resize: 'none', outline: 'none', boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
              <button
                onClick={submitComment}
                disabled={saving || !draftText.trim()}
                style={{ flex: 1, padding: '6px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}
              >{saving ? '...' : 'Добавить'}</button>
              <button
                onClick={() => setPending(null)}
                style={{ padding: '6px 10px', background: '#f3f4f6', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}
              >Отмена</button>
            </div>
          </div>
        </div>
      )}

      {/* Resolved comments counter */}
      {resolved.length > 0 && (
        <div style={{ position: 'absolute', bottom: '8px', right: '8px', zIndex: 201, fontSize: '11px', color: '#9ca3af', background: '#fff', padding: '3px 8px', borderRadius: '10px', border: '1px solid #e5e7eb' }}>
          {resolved.length} решено
        </div>
      )}
    </>
  )
}
